import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackWhatsAppClick, trackEventCustom } from "../../utils/analytics";

export default function IndustrialProcessSoftware() {
  useEffect(() => {
    document.title =
      "Industrial Process Software Development Company in Pune | HexxaTech";

    const description =
      "HexxaTech Softwares develops custom industrial process software for manufacturing businesses, covering quotations, orders, BOM, inward, process vouchers, FGI, invoicing, proforma invoices and payment workflows.";

    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setMeta("description", description);

    const canonicalUrl =
      "https://hexxatech.co.in/services/industrial-process-software";

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", canonicalUrl);

    const setPropertyMeta = (property, content) => {
      let element = document.querySelector(
        `meta[property="${property}"]`
      );

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setPropertyMeta(
      "og:title",
      "Industrial Process Software Development Company in Pune | HexxaTech"
    );

    setPropertyMeta("og:description", description);
    setPropertyMeta("og:type", "website");
    setPropertyMeta("og:url", canonicalUrl);
    setPropertyMeta("og:site_name", "HexxaTech Softwares");

    const existingSchema = document.getElementById(
      "hexxatech-industrial-process-schema"
    );

    if (!existingSchema) {
      const script = document.createElement("script");

      script.id = "hexxatech-industrial-process-schema";
      script.type = "application/ld+json";

      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Industrial Process Software Development",
        serviceType: "Industrial Process Software Development",
        description: description,
        provider: {
          "@type": "Organization",
          name: "HexxaTech Softwares",
          url: "https://hexxatech.co.in/",
        },
        areaServed: [
          {
            "@type": "City",
            name: "Pune",
          },
          {
            "@type": "Country",
            name: "India",
          },
        ],
      });

      document.head.appendChild(script);
    }

    return () => {
      const schema = document.getElementById(
        "hexxatech-industrial-process-schema"
      );

      if (schema) {
        schema.remove();
      }
    };
  }, []);

  const handleWhatsAppClick = () => {
    trackWhatsAppClick("industrial_process_software");
  };

  const handleFeatureClick = (feature) => {
    trackEventCustom("industrial_process_feature_interest", {
      feature,
    });
  };

  const features = [
    {
      number: "01",
      title: "Quotation Management",
      text: "Create, manage and track customer quotations with item details, pricing, taxes, revisions and commercial information.",
    },
    {
      number: "02",
      title: "Sales Order Management",
      text: "Convert approved quotations into sales orders and maintain complete visibility of order status, quantities and customer requirements.",
    },
    {
      number: "03",
      title: "Bill of Materials",
      text: "Manage BOM structures, raw materials, components and production requirements for different products and assemblies.",
    },
    {
      number: "04",
      title: "Material Inward",
      text: "Record incoming materials, quantities, suppliers and inventory movements while maintaining traceability across the workflow.",
    },
    {
      number: "05",
      title: "Process & Production Vouchers",
      text: "Digitize production and process activities with structured vouchers that capture movement, processing and operational information.",
    },
    {
      number: "06",
      title: "Finished Goods Management",
      text: "Track finished goods generated from production processes and maintain accurate stock and transaction information.",
    },
    {
      number: "07",
      title: "Invoice & Proforma Management",
      text: "Manage invoices and proforma invoices while connecting billing information with orders, products and customer records.",
    },
    {
      number: "08",
      title: "Payment & Receipt Tracking",
      text: "Record customer payments and receipts with transaction history and improved visibility into outstanding business amounts.",
    },
  ];

  const workflow = [
    "Customer Requirement",
    "Quotation",
    "Quotation Revision",
    "Sales Order",
    "Bill of Materials",
    "Material Inward",
    "Process / Production",
    "Finished Goods",
    "Invoice / Proforma",
    "Payment Receipt",
  ];

  const benefits = [
    {
      title: "Centralized Operations",
      text: "Bring sales, production, inventory and finance-related workflows into one connected platform.",
    },
    {
      title: "Improved Traceability",
      text: "Track materials, orders, processes and transactions across different stages of the business.",
    },
    {
      title: "Reduced Manual Work",
      text: "Replace spreadsheets and disconnected records with structured digital workflows.",
    },
    {
      title: "Real-Time Visibility",
      text: "Give management and teams better visibility into orders, stock, production and payments.",
    },
    {
      title: "Workflow Automation",
      text: "Automate repetitive operational activities and reduce dependency on manual data entry.",
    },
    {
      title: "Scalable Architecture",
      text: "Build the software so additional modules, users, reports and integrations can be added as the business grows.",
    },
  ];

  const industries = [
    "Manufacturing Companies",
    "Engineering Businesses",
    "Industrial Suppliers",
    "Fabrication Companies",
    "Machine Manufacturers",
    "Process Manufacturing",
    "Component Manufacturers",
    "Industrial Equipment Businesses",
  ];

  const serviceNavigation = [
    { title: "Custom Software Development", to: "/services/custom-software-development" },
    { title: "ERP & Business Management", to: "/services/erp-business-management" },
    { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
    { title: "Healthcare Software", to: "/services/healthcare-software" },
    { title: "Inventory Management", to: "/services/inventory-management" },
    { title: "Industrial Process Software", to: "/services/industrial-process-software" },
    { title: "License Management", to: "/services/license-management" },
    { title: "Mobile App Development", to: "/services/mobile-app-development" },
  ];

  const currentIndex = serviceNavigation.findIndex(
    (service) => service.to === "/services/industrial-process-software"
  );
  const previousService =
    serviceNavigation[(currentIndex - 1 + serviceNavigation.length) % serviceNavigation.length];
  const nextService =
    serviceNavigation[(currentIndex + 1) % serviceNavigation.length];

  const faqs = [
    {
      question:
        "What is industrial process management software?",
      answer:
        "Industrial process management software is a customized business application that connects operational workflows such as quotations, orders, BOM, material inward, production processes, finished goods, invoicing and payment tracking into one system.",
    },
    {
      question:
        "Can industrial software be customized according to our existing workflow?",
      answer:
        "Yes. HexxaTech develops industrial software around the actual workflow of the business. Modules, approvals, calculations, reports, database structures and user permissions can be designed according to specific requirements.",
    },
    {
      question:
        "Can quotation and order management be connected with production?",
      answer:
        "Yes. A custom system can connect quotations and sales orders with BOM, material requirements, production processes, finished goods and subsequent billing workflows.",
    },
    {
      question:
        "Can the software manage BOM and production processes?",
      answer:
        "Yes. The system can maintain BOM structures, components, materials, process vouchers, production activities and finished goods based on the organization's operational requirements.",
    },
    {
      question:
        "Can industrial process software integrate with existing systems?",
      answer:
        "Yes. Custom APIs and integrations can be developed to connect industrial process software with existing databases, accounting systems, applications, hardware or other business platforms.",
    },
    {
      question:
        "Does HexxaTech provide industrial software development in Pune?",
      answer:
        "Yes. HexxaTech Softwares provides custom industrial software development services in Pune and develops solutions for businesses across India.",
    },
  ];

  return (
    <div className="industrial-process-page">
      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        .industrial-process-page {
          background: #ffffff;
          color: #0f172a;
          overflow: hidden;
          font-family: Inter, -apple-system, BlinkMacSystemFont,
            "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .industrial-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =========================
           HERO
        ========================= */

        .industrial-hero {
          position: relative;
          padding: 105px 0 95px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 80% 20%,
              rgba(59, 130, 246, 0.22),
              transparent 32%
            ),
            linear-gradient(
              135deg,
              #162c4d 0%,
              #294a7d 55%,
              #1e3a63 100%
            );
        }

        .industrial-hero::before {
          content: "";
          position: absolute;
          width: 450px;
          height: 450px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.07);
          right: -170px;
          top: -190px;
        }

        .industrial-hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.05fr .95fr;
          gap: 70px;
          align-items: center;
        }

        .industrial-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 13px;
          border-radius: 999px;
          background: rgba(255,255,255,.09);
          border: 1px solid rgba(255,255,255,.15);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.1px;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        .industrial-eyebrow-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .industrial-hero h1 {
          margin: 0;
          max-width: 750px;
          font-size: clamp(42px, 5vw, 66px);
          line-height: 1.04;
          letter-spacing: -2.8px;
          font-weight: 800;
        }

        .industrial-hero h1 span {
          color: #60a5fa;
        }

        .industrial-hero-text {
          max-width: 650px;
          margin: 25px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .industrial-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .industrial-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 21px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          transition: .25s ease;
        }

        .industrial-btn-primary {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 12px 25px rgba(15,23,42,.22);
        }

        .industrial-btn-primary:hover {
          background: #1d4ed8;
          transform: translateY(-2px);
        }

        .industrial-btn-light {
          color: #ffffff;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.20);
        }

        .industrial-btn-light:hover {
          background: rgba(255,255,255,.14);
        }

        /* =========================
           HERO VISUAL
        ========================= */

        .industrial-visual {
          position: relative;
          min-height: 390px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .industrial-system-card {
          width: min(480px, 100%);
          padding: 18px;
          border-radius: 22px;
          background: rgba(255,255,255,.09);
          border: 1px solid rgba(255,255,255,.17);
          backdrop-filter: blur(12px);
          box-shadow: 0 30px 70px rgba(15,23,42,.25);
          transform: rotate(-2deg);
        }

        .industrial-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 15px;
          border-bottom: 1px solid rgba(255,255,255,.10);
        }

        .industrial-card-title {
          font-size: 13px;
          font-weight: 800;
        }

        .industrial-status {
          padding: 5px 8px;
          border-radius: 999px;
          background: rgba(96,165,250,.15);
          color: #bfdbfe;
          font-size: 9px;
          font-weight: 800;
        }

        .industrial-system-body {
          padding-top: 15px;
          display: grid;
          gap: 11px;
        }

        .industrial-flow-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 13px;
          border-radius: 11px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.08);
        }

        .industrial-flow-number {
          width: 31px;
          height: 31px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: rgba(37,99,235,.75);
          color: #ffffff;
          font-size: 10px;
          font-weight: 800;
        }

        .industrial-flow-content {
          flex: 1;
        }

        .industrial-flow-content strong {
          display: block;
          font-size: 11px;
          margin-bottom: 4px;
        }

        .industrial-flow-content span {
          display: block;
          color: #cbd5e1;
          font-size: 9px;
        }

        .industrial-progress {
          width: 70px;
          height: 5px;
          border-radius: 999px;
          background: rgba(255,255,255,.10);
          overflow: hidden;
        }

        .industrial-progress span {
          display: block;
          height: 100%;
          width: 75%;
          background: #60a5fa;
        }

        /* =========================
           QUICK STATS
        ========================= */

        .industrial-stats-wrap {
          position: relative;
          z-index: 4;
          margin-top: -38px;
        }

        .industrial-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          overflow: hidden;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          background: #ffffff;
          box-shadow: 0 18px 50px rgba(15,23,42,.08);
        }

        .industrial-stat {
          padding: 27px 20px;
          text-align: center;
          border-right: 1px solid #e5e7eb;
        }

        .industrial-stat:last-child {
          border-right: 0;
        }

        .industrial-stat strong {
          display: block;
          color: #2563eb;
          font-size: 27px;
          margin-bottom: 5px;
        }

        .industrial-stat span {
          color: #64748b;
          font-size: 12px;
        }

        /* =========================
           COMMON
        ========================= */

        .industrial-section {
          padding: 90px 0;
        }

        .industrial-section-soft {
          background: #f8fafc;
        }

        .industrial-label {
          display: inline-block;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .industrial-title {
          margin: 12px 0 0;
          max-width: 780px;
          color: #0f172a;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
        }

        .industrial-description {
          max-width: 720px;
          margin: 18px 0 0;
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
        }

        /* =========================
           INTRO
        ========================= */

        .industrial-intro-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: center;
        }

        .industrial-intro-text {
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
          margin-top: 20px;
        }

        .industrial-intro-list {
          display: grid;
          gap: 11px;
          margin-top: 25px;
        }

        .industrial-intro-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 10px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 13px;
        }

        .industrial-check {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
        }

        .industrial-overview-card {
          padding: 32px;
          border-radius: 22px;
          border: 1px solid #dbeafe;
          background: linear-gradient(145deg,#eff6ff,#ffffff);
        }

        .industrial-overview-card h3 {
          margin: 0 0 20px;
          font-size: 21px;
        }

        .industrial-overview-grid {
          display: grid;
          grid-template-columns: repeat(2,1fr);
          gap: 10px;
        }

        .industrial-overview-item {
          padding: 14px;
          border-radius: 11px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 12px;
          line-height: 1.5;
        }

        .industrial-overview-item strong {
          display: block;
          color: #2563eb;
          font-size: 11px;
          margin-bottom: 4px;
        }

        /* =========================
           FEATURES
        ========================= */

        .industrial-feature-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 45px;
        }

        .industrial-feature-header .industrial-title {
          margin-left: auto;
          margin-right: auto;
        }

        .industrial-feature-header .industrial-description {
          margin-left: auto;
          margin-right: auto;
        }

        .industrial-feature-grid {
          display: grid;
          grid-template-columns: repeat(4,1fr);
          gap: 16px;
        }

        .industrial-feature {
          position: relative;
          min-height: 235px;
          padding: 25px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: .3s ease;
        }

        .industrial-feature:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15,23,42,.07);
        }

        .industrial-feature-number {
          color: #2563eb;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .industrial-feature h3 {
          margin: 16px 0 9px;
          font-size: 16px;
        }

        .industrial-feature p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.75;
        }

        /* =========================
           WORKFLOW
        ========================= */

        .industrial-workflow-grid {
          display: grid;
          grid-template-columns: .85fr 1.15fr;
          gap: 70px;
          align-items: start;
        }

        .industrial-workflow {
          margin-top: 30px;
          display: grid;
          gap: 8px;
        }

        .industrial-workflow-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 13px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
        }

        .industrial-workflow-number {
          width: 30px;
          height: 30px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 10px;
          font-weight: 800;
        }

        .industrial-workflow-row strong {
          font-size: 12px;
        }

        .industrial-workflow-visual {
          padding: 35px;
          border-radius: 24px;
          background:
            radial-gradient(
              circle at 80% 10%,
              rgba(37,99,235,.10),
              transparent 35%
            ),
            #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 20px 50px rgba(15,23,42,.06);
        }

        .industrial-workflow-visual h3 {
          margin: 12px 0 10px;
          font-size: 23px;
        }

        .industrial-workflow-visual p {
          margin: 0 0 25px;
          color: #64748b;
          font-size: 13px;
          line-height: 1.75;
        }

        .industrial-flow-diagram {
          display: grid;
          grid-template-columns: repeat(2,1fr);
          gap: 10px;
        }

        .industrial-diagram-item {
          padding: 14px;
          border-radius: 11px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
        }

        .industrial-diagram-item strong {
          display: block;
          font-size: 12px;
          margin-bottom: 4px;
        }

        .industrial-diagram-item span {
          color: #64748b;
          font-size: 10px;
        }

        /* =========================
           BENEFITS
        ========================= */

        .industrial-benefit-grid {
          display: grid;
          grid-template-columns: repeat(3,1fr);
          gap: 18px;
          margin-top: 42px;
        }

        .industrial-benefit {
          padding: 25px;
          border-radius: 16px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
        }

        .industrial-benefit-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          background: #eff6ff;
          color: #2563eb;
          font-weight: 800;
          margin-bottom: 17px;
        }

        .industrial-benefit h3 {
          margin: 0 0 8px;
          font-size: 16px;
        }

        .industrial-benefit p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.75;
        }

        /* =========================
           INDUSTRIES
        ========================= */

        .industrial-industries {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 32px;
        }

        .industrial-industry {
          padding: 11px 14px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        /* =========================
           CTA
        ========================= */

        .industrial-cta {
          padding: 85px 0;
        }

        .industrial-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          text-align: center;
          border-radius: 24px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59,130,246,.30),
              transparent 30%
            ),
            linear-gradient(135deg,#0f172a,#172554);
        }

        .industrial-cta-box::before {
          content: "";
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.07);
          top: -170px;
          left: -80px;
        }

        .industrial-cta-box > * {
          position: relative;
          z-index: 1;
        }

        .industrial-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.10);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .industrial-cta h2 {
          max-width: 760px;
          margin: 20px auto 12px;
          font-size: clamp(34px,4vw,50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .industrial-cta p {
          max-width: 680px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .industrial-cta .industrial-btn-primary {
          background: #ffffff;
          color: #0f172a;
        }

        .industrial-cta .industrial-btn-primary:hover {
          background: #eff6ff;
        }

        /* =========================
           FAQ
        ========================= */

        .industrial-faq-header {
          max-width: 750px;
          margin-bottom: 40px;
        }

        .industrial-faq-list {
          max-width: 900px;
          display: grid;
          gap: 12px;
        }

        .industrial-faq {
          padding: 22px;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: #ffffff;
        }

        .industrial-faq h3 {
          margin: 0 0 9px;
          font-size: 15px;
          color: #0f172a;
        }

        .industrial-faq p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.75;
        }

        /* =========================
           SEO CONTENT
        ========================= */

        .industrial-seo-content {
          padding: 0 0 75px;
          background: #ffffff;
        }

        .industrial-seo-inner {
          max-width: 900px;
          margin: 0 auto;
        }

        .industrial-seo-inner h2 {
          margin: 0 0 15px;
          font-size: 24px;
        }

        .industrial-seo-inner h3 {
          margin: 30px 0 10px;
          font-size: 19px;
        }

        .industrial-seo-inner p {
          margin: 0 0 14px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.85;
        }

        /* =========================
           RESPONSIVE
        ========================= */

        @media (max-width: 1050px) {
          .industrial-feature-grid {
            grid-template-columns: repeat(2,1fr);
          }

          .industrial-hero-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .industrial-visual {
            max-width: 600px;
          }

          .industrial-intro-grid,
          .industrial-workflow-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .industrial-benefit-grid {
            grid-template-columns: repeat(2,1fr);
          }
        }

        @media (max-width: 700px) {
          .industrial-container {
            width: min(100% - 28px, 1180px);
          }

          .industrial-hero {
            padding: 75px 0 65px;
          }

          .industrial-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .industrial-hero-text {
            font-size: 15px;
          }

          .industrial-system-card {
            transform: none;
          }

          .industrial-stats {
            grid-template-columns: 1fr 1fr;
          }

          .industrial-stat {
            border-bottom: 1px solid #e5e7eb;
          }

          .industrial-stat:nth-child(2) {
            border-right: 0;
          }

          .industrial-stat:nth-child(3),
          .industrial-stat:nth-child(4) {
            border-bottom: 0;
          }

          .industrial-section {
            padding: 65px 0;
          }

          .industrial-feature-grid,
          .industrial-benefit-grid {
            grid-template-columns: 1fr;
          }

          .industrial-overview-grid,
          .industrial-flow-diagram {
            grid-template-columns: 1fr;
          }

          .industrial-overview-card,
          .industrial-workflow-visual {
            padding: 22px;
          }

          .industrial-cta {
            padding: 65px 0;
          }

          .industrial-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }
        }
      `}</style>

      {/* =========================================
          HERO
      ========================================= */}

      <section className="industrial-hero">
        <div className="industrial-container industrial-hero-grid">
          <div>
            <div className="industrial-eyebrow">
              <span className="industrial-eyebrow-dot"></span>
              Industrial Process Software
            </div>

            <h1>
              Connect your
              <br />
              <span>industrial operations.</span>
            </h1>

            <p className="industrial-hero-text">
              Custom industrial process software designed to connect
              quotations, orders, BOM, material inward, production,
              finished goods, invoicing and payment workflows in one
              business system.
            </p>

            <div className="industrial-buttons">
              <a
                href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20an%20industrial%20process%20software%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="industrial-btn industrial-btn-primary"
                onClick={handleWhatsAppClick}
              >
                Discuss Your Requirement →
              </a>

              <Link
                to="/case-studies/process-control"
                className="industrial-btn industrial-btn-light"
                onClick={() =>
                  trackEventCustom("industrial_case_study_click", {
                    location: "industrial_hero",
                  })
                }
              >
                View Process Control Case Study →
              </Link>
            </div>
          </div>

          <div className="industrial-visual">
            <div className="industrial-system-card">
              <div className="industrial-card-header">
                <div className="industrial-card-title">
                  Industrial Process System
                </div>

                <div className="industrial-status">
                  LIVE WORKFLOW
                </div>
              </div>

              <div className="industrial-system-body">
                {[
                  ["01", "Quotation", "Customer commercial requirement"],
                  ["02", "Sales Order", "Approved customer order"],
                  ["03", "BOM", "Materials & components"],
                  ["04", "Production", "Process execution"],
                  ["05", "Finished Goods", "Production completion"],
                  ["06", "Invoice", "Billing & payment"],
                ].map((item) => (
                  <div
                    className="industrial-flow-card"
                    key={item[1]}
                  >
                    <div className="industrial-flow-number">
                      {item[0]}
                    </div>

                    <div className="industrial-flow-content">
                      <strong>{item[1]}</strong>
                      <span>{item[2]}</span>
                    </div>

                    <div className="industrial-progress">
                      <span></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          STATS
      ========================================= */}

      <section className="industrial-stats-wrap">
        <div className="industrial-container">
          <div className="industrial-stats">
            <div className="industrial-stat">
              <strong>ERP</strong>
              <span>Industrial Operations</span>
            </div>

            <div className="industrial-stat">
              <strong>BOM</strong>
              <span>Material Planning</span>
            </div>

            <div className="industrial-stat">
              <strong>FGI</strong>
              <span>Finished Goods</span>
            </div>

            <div className="industrial-stat">
              <strong>24/7</strong>
              <span>Business Visibility</span>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          INTRODUCTION
      ========================================= */}

      <section className="industrial-section">
        <div className="industrial-container industrial-intro-grid">
          <div>
            <span className="industrial-label">
              Industrial Software Development
            </span>

            <h2 className="industrial-title">
              Software that follows your production workflow.
            </h2>

            <p className="industrial-intro-text">
              Industrial businesses often manage quotations, orders,
              materials, production processes, finished goods,
              invoicing and payments across multiple spreadsheets and
              disconnected systems.
            </p>

            <p className="industrial-intro-text">
              HexxaTech Softwares develops custom industrial process
              management software that connects these activities into a
              structured digital workflow.
            </p>

            <div className="industrial-intro-list">
              {[
                "Centralized industrial operations",
                "Connected quotation-to-payment workflow",
                "BOM and material tracking",
                "Production and process monitoring",
                "Finished goods and inventory visibility",
                "Customized reports and dashboards",
              ].map((item) => (
                <div
                  className="industrial-intro-item"
                  key={item}
                >
                  <span className="industrial-check">✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="industrial-overview-card">
            <h3>
              One system for connected operations
            </h3>

            <div className="industrial-overview-grid">
              <div className="industrial-overview-item">
                <strong>Sales</strong>
                Quotations and sales orders
              </div>

              <div className="industrial-overview-item">
                <strong>Materials</strong>
                Inward and stock movements
              </div>

              <div className="industrial-overview-item">
                <strong>Production</strong>
                Process and production vouchers
              </div>

              <div className="industrial-overview-item">
                <strong>BOM</strong>
                Components and requirements
              </div>

              <div className="industrial-overview-item">
                <strong>Finished Goods</strong>
                FGI and production output
              </div>

              <div className="industrial-overview-item">
                <strong>Billing</strong>
                Invoice and proforma
              </div>

              <div className="industrial-overview-item">
                <strong>Payments</strong>
                Receipts and transaction tracking
              </div>

              <div className="industrial-overview-item">
                <strong>Reports</strong>
                Management visibility
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          FEATURES
      ========================================= */}

      <section className="industrial-section industrial-section-soft">
        <div className="industrial-container">
          <div className="industrial-feature-header">
            <span className="industrial-label">
              Core Modules
            </span>

            <h2 className="industrial-title">
              Everything your industrial workflow needs.
            </h2>

            <p className="industrial-description">
              Build a centralized industrial management platform
              covering commercial, material, production, inventory and
              financial workflows.
            </p>
          </div>

          <div className="industrial-feature-grid">
            {features.map((feature) => (
              <article
                className="industrial-feature"
                key={feature.number}
                onClick={() =>
                  handleFeatureClick(feature.title)
                }
              >
                <div className="industrial-feature-number">
                  {feature.number}
                </div>

                <h3>{feature.title}</h3>

                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          WORKFLOW
      ========================================= */}

      <section className="industrial-section">
        <div className="industrial-container industrial-workflow-grid">
          <div>
            <span className="industrial-label">
              Connected Workflow
            </span>

            <h2 className="industrial-title">
              From quotation to payment.
            </h2>

            <p className="industrial-description">
              Connect the complete business lifecycle so information
              moves logically from one stage to the next without
              repeatedly entering the same data.
            </p>

            <div className="industrial-workflow">
              {workflow.map((item, index) => (
                <div
                  className="industrial-workflow-row"
                  key={item}
                >
                  <div className="industrial-workflow-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <strong>{item}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="industrial-workflow-visual">
            <span className="industrial-label">
              Process Architecture
            </span>

            <h3>
              One connected source of operational data.
            </h3>

            <p>
              Your teams can work with the same centralized business
              information while management gets a clearer view of
              operational progress and financial activity.
            </p>

            <div className="industrial-flow-diagram">
              <div className="industrial-diagram-item">
                <strong>Commercial</strong>
                <span>
                  Quotations, revisions and orders
                </span>
              </div>

              <div className="industrial-diagram-item">
                <strong>Materials</strong>
                <span>
                  Purchases, inward and stock
                </span>
              </div>

              <div className="industrial-diagram-item">
                <strong>Production</strong>
                <span>
                  Process vouchers and activities
                </span>
              </div>

              <div className="industrial-diagram-item">
                <strong>Inventory</strong>
                <span>
                  FGI and material movement
                </span>
              </div>

              <div className="industrial-diagram-item">
                <strong>Finance</strong>
                <span>
                  Invoices, proforma and receipts
                </span>
              </div>

              <div className="industrial-diagram-item">
                <strong>Management</strong>
                <span>
                  Reports and dashboards
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          BENEFITS
      ========================================= */}

      <section className="industrial-section industrial-section-soft">
        <div className="industrial-container">
          <span className="industrial-label">
            Business Benefits
          </span>

          <h2 className="industrial-title">
            Built to improve operational visibility.
          </h2>

          <p className="industrial-description">
            Custom industrial software is not just about digitizing
            existing records. It should make the overall workflow
            easier to manage, monitor and scale.
          </p>

          <div className="industrial-benefit-grid">
            {benefits.map((benefit, index) => (
              <article
                className="industrial-benefit"
                key={benefit.title}
              >
                <div className="industrial-benefit-icon">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>{benefit.title}</h3>

                <p>{benefit.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          INDUSTRIES
      ========================================= */}

      <section className="industrial-section">
        <div className="industrial-container">
          <span className="industrial-label">
            Industries We Support
          </span>

          <h2 className="industrial-title">
            Industrial software for different manufacturing environments.
          </h2>

          <p className="industrial-description">
            Our software can be customized for different industrial
            workflows, production models, approval structures and
            operational requirements.
          </p>

          <div className="industrial-industries">
            {industries.map((industry) => (
              <span
                className="industrial-industry"
                key={industry}
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          SERVICE NAVIGATION
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <span className="industrial-label">
            Explore Our Services
          </span>

          <h2 className="industrial-title" style={{ marginTop: "12px" }}>
            Explore our other software development services.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginTop: "22px",
            }}
          >
            {serviceNavigation.map((service) => {
              const isCurrent = service.to === "/services/industrial-process-software";

              return (
                <Link
                  key={service.to}
                  to={service.to}
                  style={{
                    display: "block",
                    padding: "16px 18px",
                    borderRadius: "12px",
                    textDecoration: "none",
                    border: isCurrent ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: isCurrent ? "#eff6ff" : "#f8fafc",
                    color: isCurrent ? "#1d4ed8" : "#334155",
                    fontWeight: isCurrent ? 700 : 600,
                  }}
                >
                  {service.title}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "20px",
            }}
          >
            <Link
              to={previousService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              ← Previous: {previousService.title}
            </Link>

            <Link
              to={nextService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              Next: {nextService.title} →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          CTA
      ========================================= */}

      <section className="industrial-cta">
        <div className="industrial-container">
          <div className="industrial-cta-box">
            <span className="industrial-cta-label">
              Start Your Industrial Software Project
            </span>

            <h2>
              Ready to digitize your industrial workflow?
            </h2>

            <p>
              Tell us how your quotations, orders, materials,
              production, inventory and billing currently work. We can
              help design a custom industrial process software
              solution around your business.
            </p>

            <a
              href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20an%20industrial%20process%20software%20project."
              target="_blank"
              rel="noopener noreferrer"
              className="industrial-btn industrial-btn-primary"
              onClick={handleWhatsAppClick}
            >
              Discuss Your Requirement →
            </a>
          </div>
        </div>
      </section>

      {/* =========================================
          FAQ
      ========================================= */}

      <section className="industrial-section industrial-section-soft">
        <div className="industrial-container">
          <div className="industrial-faq-header">
            <span className="industrial-label">
              Frequently Asked Questions
            </span>

            <h2 className="industrial-title">
              Industrial Process Software FAQs
            </h2>

            <p className="industrial-description">
              Common questions about custom industrial process
              management software development.
            </p>
          </div>

          <div className="industrial-faq-list">
            {faqs.map((faq) => (
              <article
                className="industrial-faq"
                key={faq.question}
              >
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          SEO CONTENT
      ========================================= */}

      <section className="industrial-seo-content">
        <div className="industrial-container industrial-seo-inner">
          <h2>
            Industrial Process Software Development Company in Pune
          </h2>

          <p>
            HexxaTech Softwares develops custom industrial process
            software for manufacturing and industrial businesses in
            Pune and across India. Our solutions are designed around
            real operational workflows including quotations, quotation
            revisions, sales orders, bill of materials, material
            inward, process vouchers, production activities, finished
            goods, invoices, proforma invoices and payment receipts.
          </p>

          <p>
            Industrial businesses often depend on multiple
            spreadsheets, manual records and disconnected applications
            to manage their operations. A centralized industrial
            process management system can connect these activities,
            reduce duplicate data entry and provide better visibility
            into business operations.
          </p>

          <h3>
            Custom Manufacturing Software for Business Workflows
          </h3>

          <p>
            Every manufacturing organization has its own processes,
            approval requirements, product structures and operational
            rules. Instead of forcing the business to change its
            workflow to match generic software, HexxaTech builds
            customized software around the organization's existing
            process.
          </p>

          <p>
            The software can include custom dashboards, reports, user
            roles, approval workflows, database structures,
            calculations and integrations required by the business.
          </p>

          <h3>
            ERP and Industrial Process Management Software
          </h3>

          <p>
            Our industrial software solutions can combine ERP-style
            business management with manufacturing process workflows.
            This allows businesses to manage commercial activities,
            materials, production, inventory, billing and reporting
            through a connected platform.
          </p>

          <p>
            HexxaTech Softwares provides industrial software
            development services in Pune for businesses looking to
            automate operations, improve traceability and gain better
            visibility into their industrial processes.
          </p>
        </div>
      </section>
    </div>
  );
}